import { useState } from 'react';
import { Star, X, Loader2, Upload } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useLang } from '@/lib/LanguageContext';

export default function ReviewFormModal({ initialData, onClose, onSaved }) {
  const [name, setName] = useState(initialData?.name || '');
  const [rating, setRating] = useState(initialData?.rating || 5);
  const [comment, setComment] = useState(initialData?.comment || '');
  const [projectUrl, setProjectUrl] = useState(initialData?.project_url || '');
  const es = useLang().language === 'es';
  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(initialData?.photo_url || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let photo_url = initialData?.photo_url || '';
      if (photoFile) {
        const { file_url } = await base44.integrations.Core.UploadFile({ file: photoFile });
        photo_url = file_url;
      }
      const project_url = projectUrl.trim() && !/^https?:\/\//i.test(projectUrl.trim()) ? `https://${projectUrl.trim()}` : projectUrl.trim();
      const data = { name, rating, comment, photo_url, project_url };
      let saved;
      if (initialData?.id) {
        saved = await base44.entities.Review.update(initialData.id, data);
      } else {
        saved = await base44.entities.Review.create(data);
      }
      onSaved({ ...data, id: initialData?.id || saved.id });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl border border-border/50 bg-card p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-inter font-bold text-xl text-foreground mb-6">
          {initialData?.id ? 'Edit Review' : 'Leave a Review'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
              Your Photo
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="w-14 h-14 rounded-full overflow-hidden border border-border/50 bg-secondary/50 flex items-center justify-center shrink-0">
                {preview ? (
                  <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <span className="text-sm text-primary font-inter">Upload photo</span>
              <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
            </label>
          </div>

          <div>
            <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
              Name
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              placeholder="Your name" />
          </div>

          <div>
            <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
              Rating
            </label>
            <div className="flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <button key={i} type="button" onClick={() => setRating(i + 1)}>
                  <Star className={`w-7 h-7 ${i < rating ? 'fill-primary text-primary' : 'text-muted-foreground/40'}`} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
              Comment
            </label>
            <textarea
              rows={4}
              required
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all resize-none"
              placeholder="Share your experience..." />
          </div>

          <div>
            <label className="text-xs font-inter font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
              {es ? 'Proyecto (enlace)' : 'Project (link)'}
            </label>
            <input
              type="url"
              value={projectUrl}
              onChange={(e) => setProjectUrl(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-foreground font-inter text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              placeholder={es ? 'https://tu-proyecto.com (opcional)' : 'https://your-project.com (optional)'} />
          </div>

          {error && <p className="text-sm text-destructive font-inter">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-4 bg-primary text-primary-foreground font-inter font-semibold text-sm tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Saving...</span></> : <span>Submit Review</span>}
          </button>
        </form>
      </div>
    </div>
  );
}